import * as types from '_/vuex/mutation-types';

/**
 * Imports config.json as a javascript object
 *
 * @type  {object}
 */
const photonConfig = require('~/config/config.json');

import { config } from '_/config/config';


/**
 * Define the module state
 *
 * @type  {object}
 */
const state = {
    bodyWidth: 0,
    breadcrumbItems: [],
    /**
     * Contains info about a default locale
     *
     * @type  {Object}
     */
    defaultLocale: {},
    // If set to false 'No entries found' message will appear instead of the jstree
    hasNodes: true,
    icon: null,
    /**
     * Stores a comma separated string that can be used to fetch localised entries
     *
     * @type  {string}
     */
    localesHeaderParameter: null,
    /**
     * Stores a list of locales
     *
     * @type  {Array}
     */
    locales: [],
    /**
     * Stores a list of filtered locales.
     * Used to limit the number of localized fields in module forms.
     *
     * @type  {Array}
     */
    filteredLocales: [],
    /**
     * Stores menus data
     *
     * @type  {Object}
     */
    menus: {
        /**
         * Stores the main menu data
         *
         * @type  {Array}
         */
        mainMenu: [],

        /**
         * Stores the quick launch menu data
         *
         * @type  {Array}
         */
        quickLaunchMenu: {},

        /**
         * Stores the user menu data
         *
         * @type  {Array}
         */
        userMenu: [],
    },
    pageType: null,
    photonConfig: photonConfig,
    screenMd: 992,
    screenXs: 480,
    searchMode: false,
    sidebarExtended: true,
    subtext: null,
    title: null,
    showSessionCheckerModal: false,
    isDevEnv: (config.ENV.name === 'development'),
};

/**
 * Define the module mutations
 *
 * @type  {object}
 */
const mutations = {
    /**
     * Sets the page title
     *
     * @param  {object}  state
     * @param  {string}  title
     * @return  {void}
     */
    [types.UI_SET_TITLE](state, title) {
        state.title = title;
    },

    /**
     * Sets visibility of session checker modal
     *
     * @param  {object}  state
     * @param  {boolean}  status
     * @return  {void}
     */
    [types.UI_SET_SHOW_SESSION_CHECKER_MODAL](state, status) {
        state.showSessionCheckerModal = status;
    },

    /**
     * Stores the main menu data to state
     *
     * @param  {object}  state
     * @param  {string}  title
     * @return  {void}
     */
    [types.UI_LOAD_MAIN_MENU_SUCCESS](state, menuData) {
        state.menus.mainMenu = menuData;
    },

    /**
     * Stores the quick launch menu data to state
     *
     * @param  {object}  state
     * @param  {string}  title
     * @return  {void}
     */
    [types.UI_LOAD_QUICK_LAUNCH_MENU_SUCCESS](state, menuData) {
        state.menus.quickLaunchMenu = menuData;
    },

    /**
     * Stores the user menu data to state
     *
     * @param  {object}  state
     * @param  {string}  title
     * @return  {void}
     */
    [types.UI_LOAD_USER_MENU_SUCCESS](state, menuData) {
        state.menus.userMenu = menuData;
    },

    /**
     * Sets the subtext
     *
     * @param  {object}  state
     * @param  {string}  subtext
     * @return  {void}
     */
    [types.UI_SET_TITLE_SUBTEXT](state, subtext) {
        state.subtext = subtext;
    },

    /**
     * Sets the icon
     *
     * @param  {object}  state
     * @param  {string}  icon
     * @return  {void}
     */
    [types.UI_SET_TITLE_ICON](state, icon) {
        state.icon = icon;
    },

    /**
     * Sets the page body width
     *
     * @param  {object}  state
     * @param  {int}  newWidth         Unit-less pixel value
     * @return  {void}
     */
    [types.UI_BODY_RESIZE](state, newWidth) {
        state.bodyWidth = newWidth;
    },

    /**
     * Sets the locales state property
     *
     * @param  {object}  state
     * @param  {array}  values
     * @return  {void}
     */
    [types.UI_SET_LOCALES](state, values) {
        state.localesHeaderParameter = values.map(value => value.language.code).join(',');

        state.locales = values.map(value => {
            return {
                allowEmptyFields: value.allow_empty_fields,
                default: !!value.default,
                id: value.id,
                languageCode: value.language.code,
                languageId: value.language.id,
                name: value.anchor_text,
            };
        });

        state.defaultLocale = state.locales.filter(value => !!value.default)[0];
    },

    /**
     * Sets the filtered locales state property
     *
     * @param  {object}  state
     * @param  {array}  values
     * @return  {void}
     */
    [types.UI_SET_FILTERED_LOCALES](state, values) {
        state.filteredLocales = values;
    },

    /**
     * Sets the breadcrumb object
     *
     * @param  {object}  state
     * @param  {array}  newBreadcrumbItems    Accepts an array consisting of object(s) with text and link values
     * @return  {void}
     */
    [types.UI_UPDATE_BREADCRUMB_ITEMS](state, newBreadcrumbItems) {
        if (newBreadcrumbItems.length) {
            newBreadcrumbItems[newBreadcrumbItems.length - 1].active = true; // If breadcrumb items are passed (not empty array) mark last one as active
        }
        state.breadcrumbItems = newBreadcrumbItems;
    },

    /**
     * Sets the hasNodes state property
     */
    [types.UI_UPDATE_HAS_NODES](state, { hasNodes }) {
        state.hasNodes = hasNodes;
    },

    /**
     * Sets the sidebar state
     *
     * @param  {object}  state
     * @param  {bool}  sidebarExtended
     * @return  {void}
     */
    [types.UI_SIDEBAR_EXTENDED_STATE](state, sidebarExtended) {
        state.sidebarExtended = sidebarExtended;
    },
};

const repackTreeData = function(nodes) {
    let map = {};

    let node;

    let roots = [];

    for (var i = 0; i < nodes.length; i += 1) {
        node = nodes[i];

        node.resource_data = node.menu_link_type_name !== 'static_link'
            ? JSON.parse(node.resource_data)
            : node.resource_data;

        node.children = [];

        map[node.id] = i;

        if (node.parent_id) {
            nodes[map[node.parent_id]].children.push(node);
        } else {
            roots.push(node);
        }
    }

    return roots;
};

/**
 * Define the module getters
 *
 * @type  {object}
 */
const getters = {
    ui: state => state
};

/**
 * Import the module actions as an object containing action methods
 *
 * @type  {object}
 */
import actions from '_/vuex/actions/uiActions';

export default {
    actions,
    getters,
    mutations,
    namespaced: true,
    state
};
